import { SUPABASE_URL } from '../utils/supabase/url';
import { getAuthHeaders } from '../utils/supabase/client';
/**
 * PresenceContext
 *
 * Provides presence for all users whose IDs are registered via `watchUsers()`.
 * The server works it out (src/presence/derivePresence.ts): a status the person
 * picked, then the automatic statuses they switched on in Preferences >
 * Availability (in a meeting, on site, outside working hours), then available
 * when a calendar is connected. `state` carries it in the canonical vocabulary,
 * null meaning no dot; `status` is the legacy vocabulary older servers send.
 *
 * The context polls the backend while the tab is visible, backing off while
 * nobody's status changes, and merges results.
 * Individual components call `usePresence(userId)` to get a single user's status.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { DataService } from '../services/DataService';
import { useAdaptivePoll } from '../hooks/useAdaptivePoll';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '../shared/ui/tooltip';
import { fromLegacyPresenceStatus, presenceColourStatus } from '../presence/presenceVocabulary';
import type { ResolvedPresenceStatus } from '../types/presence';

// ── Types ──────────────────────────────────────────────────────────────────

export type PresenceStatus =
  | 'online'
  | 'in_meeting'
  | 'out_of_office'
  | 'away'
  | 'commuting'
  | 'off_sick'
  | 'on_holiday'
  | 'working_remotely'
  | 'not_connected';

export interface UserPresence {
  /** Legacy vocabulary. Prefer `presenceState()`, which reads `state` first. */
  status: PresenceStatus;
  /** Canonical vocabulary; null means show no dot. Absent from older servers. */
  state?: ResolvedPresenceStatus | null;
  label: string;
  until?: string; // ISO – when the current meeting / OOO ends
  source?: 'manual' | 'out_of_office' | 'meeting' | 'on_site' | 'after_hours' | 'default';
}

/** The canonical status to draw, or null for no dot (unknown, or appearing offline). */
export function presenceState(presence: UserPresence | null | undefined): ResolvedPresenceStatus | null {
  if (!presence) return null;
  const state = presence.state !== undefined ? presence.state : fromLegacyPresenceStatus(presence.status);
  return state === 'offline' ? null : state;
}

interface PresenceContextType {
  /** Look up presence for a single userId. Returns null while loading. */
  getPresence: (userId: string) => UserPresence | null;
  /** Register a set of userIds to be included in the next poll. */
  watchUsers: (userIds: string[]) => () => void;
  /** Force an immediate refresh. */
  refresh: () => void;
}

// ── Constants ──────────────────────────────────────────────────────────────

// Presence moves with calendars and manual picks, rarely minute to minute:
// start at two minutes and stretch to six while nobody's status changes.
const POLL_POLICY = { baseMs: 2 * 60 * 1000, maxMs: 6 * 60 * 1000 };
// A return to the tab re-reads only once the last read is a full base old.
const RETURN_MIN_GAP_MS = POLL_POLICY.baseMs;
// Rows mounting together (a member list, a chat header) share one request.
const BATCH_MS = 25;
const BASE_URL = `${SUPABASE_URL}/functions/v1/make-server-a476273b`;

// ── Context ────────────────────────────────────────────────────────────────

const PresenceContext = createContext<PresenceContextType>({
  getPresence: () => null,
  watchUsers: () => () => {},
  refresh: () => {},
});

// ── Provider ───────────────────────────────────────────────────────────────

function samePresence(a: UserPresence | undefined, b: UserPresence | undefined): boolean {
  return !!a && !!b && a.status === b.status && a.state === b.state && a.label === b.label && a.until === b.until && a.source === b.source;
}

/** Ids to read when watchers register: new to this tab, or last read longer ago than the poll base. */
export function presenceIdsToFetch(ids: Iterable<string>, fetchedAt: ReadonlyMap<string, number>, now: number): string[] {
  return [...new Set(ids)].filter(id => {
    const at = fetchedAt.get(id);
    return at === undefined || now - at >= POLL_POLICY.baseMs;
  });
}

export function PresenceProvider({ children }: { children: React.ReactNode }) {
  const [presenceMap, setPresenceMap] = useState<Record<string, UserPresence>>({});
  const presenceRef = useRef(presenceMap);
  useEffect(() => { presenceRef.current = presenceMap; }, [presenceMap]);
  // Watchers are counted per id: a dot unmounting must not drop a user that
  // another mounted component still shows, or every remount refetched it.
  const watchedRef = useRef<Map<string, number>>(new Map());
  const fetchedAtRef = useRef<Map<string, number>>(new Map());
  const pendingRef = useRef<Set<string>>(new Set());
  const batchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Reads the given users and reports whether any of them changed. */
  const readPresence = useCallback(async (ids: string[], retry: boolean): Promise<boolean> => {
    if (ids.length === 0) return false;

    // Demo session: no presence round-trips. The demo doesn't watch real
    // users, and pinging the backend would advertise the demo's online
    // state to admins.
    if (DataService.isDemoMode()) return false;

    // Stamped when the request leaves, so a batch queued behind a poll that
    // already covers these users does not ask for them again.
    const previous = new Map(ids.map(id => [id, fetchedAtRef.current.get(id)]));
    const startedAt = Date.now();
    ids.forEach(id => fetchedAtRef.current.set(id, startedAt));
    const restore = () => previous.forEach((at, id) => {
      if (fetchedAtRef.current.get(id) !== startedAt) return;
      if (at === undefined) fetchedAtRef.current.delete(id); else fetchedAtRef.current.set(id, at);
    });
    const maxRetries = retry ? 2 : 0;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const res = await fetch(
          `${BASE_URL}/presence/users?userIds=${ids.join(',')}`,
          {
            headers: await getAuthHeaders(),
            signal: AbortSignal.timeout(8000),
          }
        );
        if (!res.ok) {
          if (res.status === 503 && attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
            continue;
          }
          throw Object.assign(new Error(`Presence unavailable (${res.status})`), { name: 'PresenceHttpError' });
        }
        const data = await res.json();
        const incoming = (data.presence ?? {}) as Record<string, UserPresence>;
        const changed = Object.entries(incoming).some(([id, value]) => !samePresence(presenceRef.current[id], value));
        if (changed) setPresenceMap(prev => ({ ...prev, ...incoming }));
        return changed;
      } catch (err) {
        if (err instanceof Error &&
            err.name !== 'AbortError' &&
            err.name !== 'TimeoutError' &&
            err.name !== 'PresenceHttpError' &&
            attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
          continue;
        }
        restore();
        throw err;
      }
    }
    return false;
  }, []);

  const fetchPresence = useCallback(
    () => readPresence([...watchedRef.current.keys()], true),
    [readPresence],
  );

  // useAdaptivePoll handles visibility/online/offline via NetworkGateway.
  const trigger = useAdaptivePoll(fetchPresence, POLL_POLICY, { minGapMs: RETURN_MIN_GAP_MS });

  const getPresence = useCallback(
    (userId: string): UserPresence | null => presenceMap[userId] ?? null,
    [presenceMap]
  );

  const flushPending = useCallback(() => {
    batchTimerRef.current = null;
    const ids = presenceIdsToFetch(pendingRef.current, fetchedAtRef.current, Date.now())
      .filter(id => watchedRef.current.has(id));
    pendingRef.current.clear();
    // Best-effort first read for newly watched users; the poll picks them up
    // regardless, so a failure here is safe to ignore rather than surface.
    readPresence(ids, false).catch(() => {});
  }, [readPresence]);

  const watchUsers = useCallback((userIds: string[]): (() => void) => {
    const ids = userIds.filter(Boolean);
    for (const id of ids) {
      const count = watchedRef.current.get(id) ?? 0;
      watchedRef.current.set(id, count + 1);
      if (count === 0) pendingRef.current.add(id);
    }
    if (pendingRef.current.size > 0 && batchTimerRef.current === null) {
      batchTimerRef.current = setTimeout(flushPending, BATCH_MS);
    }
    return () => {
      for (const id of ids) {
        const count = watchedRef.current.get(id) ?? 0;
        if (count <= 1) watchedRef.current.delete(id);
        else watchedRef.current.set(id, count - 1);
      }
    };
  }, [flushPending]);

  useEffect(() => () => {
    if (batchTimerRef.current !== null) clearTimeout(batchTimerRef.current);
  }, []);

  const refresh = useCallback(() => { void trigger(); }, [trigger]);

  return (
    <PresenceContext.Provider value={{ getPresence, watchUsers, refresh }}>
      {children}
    </PresenceContext.Provider>
  );
}

// ── Hook ───────────────────────────────────────────────────────────────────

export function usePresence(userId?: string): UserPresence | null {
  const ctx = useContext(PresenceContext);
  return userId ? ctx.getPresence(userId) : null;
}

export function usePresenceActions() {
  return useContext(PresenceContext);
}

// ── Dot component ──────────────────────────────────────────────────────────

/**
 * A small coloured dot representing the user's presence status.
 * Renders nothing when status is `not_connected` or presence is unknown.
 * Shows a rich tooltip with the label + formatted "until" time on hover.
 */
export function PresenceDot({
  userId,
  size = 10,
  borderColor = 'var(--background)',
  tooltip = true,
}: {
  userId?: string;
  size?: number;
  borderColor?: string;
  tooltip?: boolean;
}) {
  const presence = usePresence(userId);
  const state = presenceState(presence);

  if (!presence || !state) return null;

  const dotColor = statusToColor(presenceColourStatus(presence));

  const dot = (
    <span
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: '50%',
        background: dotColor,
        border: `${Math.max(1.5, size * 0.18)}px solid ${borderColor}`,
        flexShrink: 0,
        cursor: tooltip ? 'default' : 'inherit',
      }}
      aria-label={presence.label}
    />
  );

  if (!tooltip) return dot;

  const tooltipText = formatPresenceTooltip(presence);

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>{dot}</TooltipTrigger>
      <TooltipContent
        side="right"
        sideOffset={8}
        style={{
          background: 'var(--popover)',
          color: 'var(--popover-foreground)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '5px 10px',
          boxShadow: 'var(--glass-shadow-value, 0 4px 16px rgba(0,0,0,0.08))',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
        className="font-apple"
      >
        <span
          className="font-apple flex items-center gap-1.5"
          style={{
            fontSize: 'var(--text-search)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--popover-foreground)',
            whiteSpace: 'nowrap',
          }}
        >
          {/* Coloured status indicator */}
          <span
            style={{
              display: 'inline-block',
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: dotColor,
              flexShrink: 0,
            }}
          />
          {tooltipText}
        </span>
      </TooltipContent>
    </Tooltip>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

/**
 * Build the full tooltip string: label + "until X" when applicable.
 *
 * Examples:
 *   "Online"
 *   "In a meeting · until 3:00 pm"
 *   "Out of office · until tomorrow"
 *   "Out of office · until Wednesday"
 *   "Out of office · until 15 Mar"
 *   "Away · outside working hours"
 */
export function formatPresenceTooltip(presence: UserPresence): string {
  const base = presence.label;

  // The label already says why someone is away ("Outside working hours").
  if (!presence.until) return base;

  const suffix = formatUntilTime(presence.until);
  return suffix ? `${base} · until ${suffix}` : base;
}

/**
 * Formats an ISO timestamp into a human-readable "until" string.
 *
 *  same day  → "3:00 pm"
 *  tomorrow  → "tomorrow"
 *  ≤6 days   → "Wednesday"
 *  otherwise → "15 Mar"
 */
export function formatUntilTime(isoString: string): string {
  try {
    const until = new Date(isoString);
    const now = new Date();

    // Clamp to midnight boundaries
    const todayStart = new Date(now);
    todayStart.setHours(0, 0, 0, 0);
    const tomorrowStart = new Date(todayStart);
    tomorrowStart.setDate(tomorrowStart.getDate() + 1);
    const dayAfterTomorrow = new Date(tomorrowStart);
    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 1);
    const sixDaysOut = new Date(todayStart);
    sixDaysOut.setDate(sixDaysOut.getDate() + 7);

    if (until < tomorrowStart) {
      // Same day — show time
      return until.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
    } else if (until < dayAfterTomorrow) {
      return 'tomorrow';
    } else if (until < sixDaysOut) {
      return until.toLocaleDateString([], { weekday: 'long' });
    } else {
      return until.toLocaleDateString([], { day: 'numeric', month: 'short' });
    }
  } catch {
    return '';
  }
}

/** Colour for a status in either vocabulary; the words they share mean the same thing. */
export function statusToColor(status: PresenceStatus | ResolvedPresenceStatus | null | undefined): string {
  switch (status) {
    case 'online':
    case 'available':     return 'var(--status-success)';
    case 'busy':
    case 'dnd':           return 'var(--status-danger)';
    case 'be_right_back': return 'var(--status-warning)';
    case 'on_site':       return 'var(--status-info)';
    case 'in_meeting':    return 'var(--status-warning)';
    case 'out_of_office': return 'var(--status-danger)';
    case 'commuting':     return 'var(--status-info)';
    case 'off_sick':      return 'var(--status-danger)';
    case 'on_holiday':    return 'var(--status-brand)';
    case 'working_remotely': return 'var(--status-success)';
    case 'away':          return 'var(--muted-foreground)';
    default:              return 'transparent';
  }
}

export function statusToLabel(status: PresenceStatus): string {
  switch (status) {
    case 'online':        return 'Online';
    case 'in_meeting':    return 'In a meeting';
    case 'out_of_office': return 'Out of office';
    case 'commuting':     return 'Commuting';
    case 'off_sick':      return 'Off sick';
    case 'on_holiday':    return 'On holiday';
    case 'working_remotely': return 'Working remotely';
    case 'away':          return 'Away';
    case 'not_connected': return '';
  }
}
